"use client";

import {
  forwardRef,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import { LottiePlayer } from "@/components/lottie-player";
import { useIntersectedOnce } from "@/hooks/use-intersection-once";
import { cn } from "@/lib/utils";

// Animation instance type (from lottie-web, used by lottieRef callback)
interface AnimationInstance {
  play: () => void;
  pause: () => void;
  stop: () => void;
}

// PlayerEvent values for use in client-side code
const PlayerEvent = {
  Load: "load",
  Complete: "complete",
} as const;

export interface AnimationPlayerHandle {
  play: () => void;
}

export interface AnimationPlayerProps {
  animation: string;
  mobileAnimation?: string | null;
  bgColor?: string | null;
  className?: string;
  loop?: boolean | null;
  autoPlay?: boolean | null;
  onComplete?: () => void;
}

export const AnimationPlayer = forwardRef<
  AnimationPlayerHandle,
  AnimationPlayerProps
>((props, ref) => {
  const {
    animation,
    mobileAnimation,
    bgColor,
    className,
    loop,
    autoPlay,
    onComplete,
  } = props;
  const containerRef = useRef(null);
  const lottieRef = useRef<AnimationInstance | null>(null);
  const mobileLottieRef = useRef<AnimationInstance | null>(null);
  const [lottieLoaded, setLottieLoaded] = useState(false);
  const [mobileLottieLoaded, setMobileLottieLoaded] = useState(false);
  const [shouldPlay, setShouldPlay] = useState(false);

  useImperativeHandle(
    ref,
    () => ({
      play: () => {
        // required to ensure that the animation plays when it is triggered externally
        // as it needs to be loaded first
        // defer playing until both animations are loaded
        if (!lottieLoaded || !mobileLottieLoaded) {
          setShouldPlay(true);
          return;
        }

        // otherwise play immediately
        lottieRef.current?.play();
        mobileLottieRef.current?.play();
      },
    }),
    [lottieLoaded, mobileLottieLoaded]
  );

  const ID = useId();

  const show = useIntersectedOnce(containerRef, {
    root: null,
    rootMargin: "0px",
    // TODO: Make this configurable
    threshold: 0.3,
  });

  useEffect(() => {
    if (!show) return;
    if (autoPlay === false && !shouldPlay) return;

    if (lottieLoaded) lottieRef.current?.play();
    if (mobileLottieLoaded) mobileLottieRef.current?.play();
  }, [show, lottieLoaded, mobileLottieLoaded, autoPlay, shouldPlay]);

  const hasMobileAnimation = !!mobileAnimation;

  return (
    <div
      ref={containerRef}
      className={cn("w-full", className)}
      style={{ backgroundColor: bgColor ?? "transparent" }}
    >
      <LottiePlayer
        lottieRef={(instance) => {
          lottieRef.current = instance;
        }}
        onEvent={(event) => {
          if (event === PlayerEvent.Load) {
            setLottieLoaded(true);
          }
          if (event === PlayerEvent.Complete) {
            onComplete?.();
            lottieRef.current?.stop();
            setShouldPlay(false);
          }
        }}
        loop={loop !== false}
        id={`${ID}-lottie`}
        src={animation}
        keepLastFrame={loop === false}
        className={cn(hasMobileAnimation && "hidden lg:block")}
      />
      {hasMobileAnimation && (
        <LottiePlayer
          lottieRef={(instance) => {
            mobileLottieRef.current = instance;
          }}
          onEvent={(event) => {
            if (event === PlayerEvent.Load) {
              setMobileLottieLoaded(true);
            }
          }}
          id={`${ID}-lottie-mobile`}
          className="lg:hidden"
          loop={loop !== false}
          keepLastFrame={loop === false}
          src={mobileAnimation}
        />
      )}
    </div>
  );
});

AnimationPlayer.displayName = "AnimationPlayer";
